/**
 * Main App Component
 *
 * Root application component with routing, authentication, and layout management.
 * Handles protected routes, authentication flow, and global state.
 *
 * Features:
 * - React Router for navigation
 * - Protected routes with authentication
 * - Main layout with campaign sidebar
 * - Dark mode support
 * - Loading states
 */

import React, { Suspense, lazy, Component, ErrorInfo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { MobileProvider } from './contexts/MobileContext';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { EmailVerificationPage } from './pages/EmailVerificationPage';
import { DashboardPage } from './pages/DashboardPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { OAuthCallback } from './pages/OAuthCallback';
import { OAuthConnect } from './pages/OAuthConnect';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { AcceptableUsePolicyPage } from './pages/AcceptableUsePolicyPage';
import { DataProcessingAgreementPage } from './pages/DataProcessingAgreementPage';
import { SubProcessorsPage } from './pages/SubProcessorsPage';
import { CookieSettingsPage } from './pages/CookieSettingsPage';
import { DataDeletionPage } from './pages/DataDeletionPage';
import { CookieConsentBanner } from './components/legal/CookieConsentBanner';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { AppLayout, AppFrameHost, FramePageLoading } from './components/layout/AppLayout';
import { SystemCard, SystemPage, systemButton } from './components/system/SystemState';
import { useMobile } from './contexts/MobileContext';
import { DeskProvider } from './components/growth-desk/data/DeskData';
import { DESK } from './components/growth-desk/links';
import { OverlayProvider, SetupDockMount } from './components/onboarding';

/** Toggle button for the campaign list sidebar — shown only on mobile in the Campaigns page top bar */
const CampaignListToggle: React.FC = () => {
  const { toggleSidebar } = useMobile();
  return (
    <button
      onClick={toggleSidebar}
      className="p-2 rounded-lg text-ink-2 hover:bg-inset transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
      aria-label="Toggle campaign list"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
      </svg>
    </button>
  );
};

// Lazy load heavy pages with Stripe/payment dependencies
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage').then(m => ({ default: m.PaymentCancelPage })));
const CampaignAnalyticsPage = lazy(() => import('./pages/CampaignAnalyticsPage').then(m => ({ default: m.CampaignAnalyticsPage })));
const LeadsPage = lazy(() => import('./pages/LeadsPage').then(m => ({ default: m.LeadsPage })));
const BusinessesSettingsPage = lazy(() => import('./pages/BusinessesSettingsPage').then(m => ({ default: m.BusinessesSettingsPage })));
const MediaLibraryPage = lazy(() => import('./pages/MediaLibraryPage').then(m => ({ default: m.MediaLibraryPage })));
let vibeKitPageImport: Promise<{ default: React.ComponentType }> | null = null;
const loadVibeKitPage = () => {
  if (!vibeKitPageImport) {
    vibeKitPageImport = import('./pages/VibeKitPage').then(m => ({ default: m.VibeKitPage }));
  }
  return vibeKitPageImport;
};
const VibeKitPage = lazy(loadVibeKitPage);

// A lazy-loaded chunk 404s (server returns index.html, hence the "text/html
// MIME type" console error) when a tab has been open since before a deploy —
// the loaded bundle references a hashed chunk filename the new deploy no
// longer serves. Re-rendering against the same stale bundle can't fix this;
// only a real reload (fresh index.html, current hashes) can. Reload once —
// the sessionStorage flag stops an infinite loop if the error persists for a
// genuinely different reason after that.
function isChunkLoadError(error: Error): boolean {
  return /dynamically imported module|Loading chunk|Failed to fetch dynamically/i.test(error.message);
}
function reloadOnceForChunkError(error: Error): boolean {
  if (!isChunkLoadError(error)) return false;
  const key = 'admeru_chunk_reload_attempted';
  if (sessionStorage.getItem(key)) return false;
  sessionStorage.setItem(key, '1');
  window.location.reload();
  return true;
}

interface LazyBoundaryState { error: Error | null; }
class LazyBoundary extends Component<{ children: React.ReactNode }, LazyBoundaryState> {
  state: LazyBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(_error: Error, _info: ErrorInfo) {
    vibeKitPageImport = null;
    reloadOnceForChunkError(_error);
  }
  retry = () => { vibeKitPageImport = null; this.setState({ error: null }); };
  render() {
    if (this.state.error) {
      return (
        <SystemPage>
          <SystemCard big="Oops." title="Ad Studio didn’t load" body="Nothing was lost or spent. Try again in a moment."
            actions={<button type="button" onClick={this.retry} className={systemButton.navy}>Try again</button>} />
        </SystemPage>
      );
    }
    return this.props.children;
  }
}

/** General error boundary — catches render errors anywhere inside the app
 *  and shows the error message instead of going blank. */
interface AppErrorBoundaryState { error: Error | null; }
class AppErrorBoundary extends Component<{ children: React.ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary] Render error:', error, info.componentStack);
    reloadOnceForChunkError(error);
  }
  retry = () => this.setState({ error: null });
  render() {
    if (this.state.error) {
      return (
        <SystemPage fullScreen>
          <SystemCard big="Oops." title="Something went wrong on our side" body="Nothing was lost or spent. Try again in a moment."
            details={this.state.error.message}
            actions={<>
              <button type="button" onClick={this.retry} className={systemButton.navy}>Try again</button>
              <a href="mailto:support@admeru.com" className={systemButton.ghost}>Contact us</a>
            </>} />
        </SystemPage>
      );
    }
    return this.props.children;
  }
}
/** 404 (system board): inside the app frame when signed in, on its own otherwise. */
const NotFoundPage: React.FC = () => {
  const { user } = useAuth();
  const card = (
    <SystemCard big="404" bigTone="brand" title="This page isn’t here" body="It may have moved when we reorganised Admeru."
      actions={<a href="/" className={systemButton.navy}>{user ? 'Go to This month' : 'Go to Admeru'}</a>} />
  );
  return user ? <AppLayout><SystemPage>{card}</SystemPage></AppLayout> : <SystemPage fullScreen>{card}</SystemPage>;
};

// The monthly loop: This month, plan, prepare, production, campaign review, calendar, business, strategy
const ThisMonthPage = lazy(() => import('./pages/growth-desk/ThisMonthPage'));
const PlanPage = lazy(() => import('./pages/growth-desk/PlanPage'));
const PreparePage = lazy(() => import('./pages/growth-desk/PreparePage'));
const ProductionPage = lazy(() => import('./pages/growth-desk/ProductionPage'));
const PostsPage = lazy(() => import('./pages/growth-desk/PostsPage'));
const NewPostPage = lazy(() => import('./pages/growth-desk/NewPostPage'));
const CampaignPage = lazy(() => import('./pages/growth-desk/CampaignPage'));
const BusinessPage = lazy(() => import('./pages/growth-desk/BusinessPage'));
const StrategyPage = lazy(() => import('./pages/growth-desk/StrategyPage'));
const StartPage = lazy(() => import('./pages/growth-desk/StartPage'));
const NeedsPage = lazy(() => import('./pages/growth-desk/NeedsPage'));
const DeskCalendarPage = lazy(() => import('./pages/growth-desk/CalendarPage'));
const GrowthDeskParts = lazy(() => import('./pages/growth-desk/PartsPage'));

/** "/" is This month for signed-in people and the landing page for visitors. */
const HomeRoute: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner size="lg" text="Loading..." fullScreen />;
  if (!user) return <LandingPage />;
  return <Suspense fallback={<FramePageLoading />}><ThisMonthPage /></Suspense>;
};

/** Old Brand Kit links (bookmarks, notifications) open where those parts live now. */
const BrandKitRedirect: React.FC = () => {
  const q = new URLSearchParams(window.location.search);
  const tab = q.get('tab'), review = q.get('review');
  const n = new Date();
  const month = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  const to = review ? `/campaigns/${review}/review`
    : tab === 'plan' ? `/month/${month}`
    : tab === 'media' ? '/media?view=planning'
    : tab === 'strategy' ? '/business?topic=connections'
    : '/business?topic=voice';
  return <Navigate to={to} replace />;
};

/** Addresses from the short-lived /desk area. */
const DeskRedirect: React.FC = () => {
  const path = window.location.pathname.replace(/^\/desk\/?/, '');
  const search = window.location.search;
  const to = !path ? `/${search}`
    : path.startsWith('plan/') ? `/month/${path.slice(5)}${search}`
    : /^campaigns\/[^/]+(\/(launch|results))?$/.test(path) ? path.replace(/^campaigns\/([^/]+)(\/(launch|results))?$/, (_m, id, _t, tab) => `/campaigns/${id}/review${tab ? `/${tab}` : ''}`)
    : `/${path}${search}`;
  return <Navigate to={to} replace />;
};

/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication. Redirects to login if user is not authenticated.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner size="lg" text="Loading..." fullScreen />;
  }

  // Check for valid JWT token in storage (not just user object)
  // A user object without a JWT token means registration succeeded but user hasn't logged in yet
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

  // Redirect to login if not authenticated (missing both user AND token)
  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * PublicRoute Component
 *
 * Wraps routes that should redirect to dashboard if user is already authenticated.
 */
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner size="lg" text="Loading..." fullScreen />;
  }

  // Redirect to dashboard if already authenticated
  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * App Component
 *
 * Main application component with all routes configured.
 */
const paymentsEnabled = import.meta.env.VITE_PAYMENTS_ENABLED !== 'false';

/** Redirect that keeps the query (?buy=1, ?plans=1). */
const KeepQueryRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

function App() {
  // A successful mount means the reload (if one happened) actually fixed
  // things — clear the flag so a stale-chunk error from some LATER deploy,
  // possibly days into the same long-lived tab, can still trigger one more
  // auto-reload instead of going straight to the manual-retry error screen.
  useEffect(() => {
    sessionStorage.removeItem('admeru_chunk_reload_attempted');
  }, []);

  return (
    <AppErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <MobileProvider>
          <DeskProvider>
          <ToastProvider>
          <ConfirmProvider>
          <OverlayProvider>
            <div className="min-h-screen bg-background-light dark:bg-background-dark">
              <CookieConsentBanner />
              <AppFrameHost>
              <Routes>
            {/* Landing Page - Public */}
            {/* Home: This month when signed in, the landing page for visitors */}
            <Route path="/" element={<HomeRoute />} />

            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />

            {/* Email Verification Page - Public route after registration */}
            <Route
              path="/verify-email"
              element={<EmailVerificationPage />}
            />

            {/* Forgot Password Page - Public route for password reset */}
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPage />}
            />

            {/* Reset Password Page - Public route for setting new password */}
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />

            {/* Privacy Policy Page - Public route */}
            <Route
              path="/privacy"
              element={<PrivacyPolicyPage />}
            />
            <Route
              path="/privacy-policy"
              element={<PrivacyPolicyPage />}
            />

            {/* Terms of Service Page - Public route */}
            <Route
              path="/terms"
              element={<TermsOfServicePage />}
            />
            <Route
              path="/terms-of-service"
              element={<TermsOfServicePage />}
            />

            {/* Cookie Policy Page - Public route */}
            <Route
              path="/cookie-policy"
              element={<CookiePolicyPage />}
            />
            <Route
              path="/cookies"
              element={<CookiePolicyPage />}
            />

            {/* Cookie Settings Page - Public route */}
            <Route
              path="/cookie-settings"
              element={<CookieSettingsPage />}
            />

            {/* Sub-Processors Page - Public route */}
            <Route
              path="/sub-processors"
              element={<SubProcessorsPage />}
            />

            {/* Acceptable Use Policy Page - Public route */}
            <Route
              path="/acceptable-use"
              element={<AcceptableUsePolicyPage />}
            />
            <Route
              path="/acceptable-use-policy"
              element={<AcceptableUsePolicyPage />}
            />

            {/* Data Processing Agreement - Public route */}
            <Route
              path="/dpa"
              element={<DataProcessingAgreementPage />}
            />

            {/* Data Deletion Policy - Public route (Meta Platform Terms compliance) */}
            <Route
              path="/data-deletion"
              element={<DataDeletionPage />}
            />

            {/* Checkout Page - Protected: requires login before upgrading */}
            <Route
              path="/checkout"
              element={
                paymentsEnabled ? (
                  <ProtectedRoute>
                    <AppLayout focused>
                      <Suspense fallback={<LoadingSpinner />}>
                        <CheckoutPage />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Payment Success/Cancel Pages */}
            <Route
              path="/payment/success"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PaymentSuccessPage />
                </Suspense>
              }
            />
            <Route
              path="/payment/cancel"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <PaymentCancelPage />
                </Suspense>
              }
            />

            {/* Growth Desk — new area alongside today's pages */}
            <Route path="/desk/*" element={<DeskRedirect />} />
<Route
              path="/month/:month"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <PlanPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/month/:month/prepare"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <PreparePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/month/:month/posts"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <PostsPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {['/month/:month/posts/new', '/month/:month/posts/:postId/make'].map(path => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<FramePageLoading />}>
                      <NewPostPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path="/month/:month/production"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <ProductionPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:productionId/review"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <CampaignPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:productionId/review/:tab"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <CampaignPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* A post's review: the same page as a campaign's, at its own address */}
            <Route
              path="/posts/:productionId"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <CampaignPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Posts: this month's posts page */}
            <Route path="/posts" element={<Navigate to={DESK.posts()} replace />} />
            <Route
              path="/business"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <BusinessPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <StrategyPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/start"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <StartPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/needs"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <NeedsPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Growth Desk parts gallery (dev only) */}
            {import.meta.env.DEV && (
              <Route
                path="/dev/parts"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <GrowthDeskParts />
                  </Suspense>
                }
              />
            )}

            {/* Dashboard */}
            <Route
              path="/results"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <AppLayout><DashboardPage /></AppLayout>
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />

            {/* Media Library */}
            <Route
              path="/media"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Suspense fallback={<FramePageLoading />}>
                      <MediaLibraryPage />
                    </Suspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Ad Studio — Brand ad template creation */}
            <Route
              path="/ad-studio"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <LazyBoundary>
                      <Suspense fallback={<FramePageLoading />}>
                        <VibeKitPage />
                      </Suspense>
                    </LazyBoundary>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Old Brand Kit links → Your business */}
            <Route path="/brand-kit" element={<BrandKitRedirect />} />

            {/* Calendar */}
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<FramePageLoading />}>
                    <DeskCalendarPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Campaigns List */}
            <Route
              path="/campaigns"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CampaignsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Campaign with ID (includes 'new') */}
            <Route
              path="/campaigns/:campaignId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CampaignsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Leads captured from ads */}
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Suspense fallback={<FramePageLoading />}>
                      <LeadsPage />
                    </Suspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Campaign Analytics */}
            <Route
              path="/campaigns/:campaignId/analytics"
              element={
                <ProtectedRoute>
                  <AppLayout pageTitle="Analytics">
                    <Suspense fallback={<FramePageLoading />}>
                      <CampaignAnalyticsPage />
                    </Suspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Settings */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Suspense fallback={<FramePageLoading />}>
                      <SettingsPage />
                    </Suspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Settings › Businesses: any business's connections, defaults, team, notifications */}
            <Route
              path="/settings/businesses/:orgId?/:section?"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Suspense fallback={<FramePageLoading />}>
                      <BusinessesSettingsPage />
                    </Suspense>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Settings › Plan & billing */}
            <Route
              path="/settings/billing"
              element={
                paymentsEnabled ? (
                  <ProtectedRoute>
                    <AppLayout>
                      <Suspense fallback={<FramePageLoading />}>
                        <SubscriptionPage />
                      </Suspense>
                    </AppLayout>
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Old settings addresses */}
            <Route path="/settings/organizations" element={<Navigate to="/settings/businesses" replace />} />
            <Route path="/team" element={<Navigate to="/settings/businesses/active/team" replace />} />
            <Route path="/subscription" element={<KeepQueryRedirect to="/settings/billing" />} />

            {/* Purchase Credits */}
            <Route
              path="/purchase-credits"
              element={
                paymentsEnabled ? (
                  // Credit packs live on Plan, billing & credits.
                  <Navigate to="/settings/billing?buy=1" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Redirect legacy /billing/upgrade URLs to Plan & billing */}
            <Route path="/billing/upgrade" element={<Navigate to="/settings/billing" replace />} />

            {/* OAuth Connect - Initiate OAuth flow */}
            <Route
              path="/oauth/connect"
              element={
                <ProtectedRoute>
                  <AppLayout focused>
                    <OAuthConnect />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* OAuth Callback */}
            <Route
              path="/oauth/callback"
              element={
                <ProtectedRoute>
                  <OAuthCallback />
                </ProtectedRoute>
              }
            />

            {/* 404 Not Found */}
            <Route
              path="*"
              element={<NotFoundPage />
              }
            />
              </Routes>
              </AppFrameHost>
              <SetupDockMount />
            </div>
          </OverlayProvider>
          </ConfirmProvider>
          </ToastProvider>
          </DeskProvider>
          </MobileProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
